import { IMortgage } from "@/utilities/types/Mortgage.ts";
import { Roles } from "@/utilities/types/Users.ts";
import { HeaderCell } from "@/components/ui/table.tsx";
import useProfile from "@/hooks/use-profile.ts";
import DeletePopover from "@/app/shared/delete-popover.tsx";
import { ActionIcon, Tooltip } from "rizzui";
import PencilIcon from "@/components/icons/pencil";
import { EPermissionApiKey } from "@/utilities/types/ApiKey";

type Columns = {
  data: any;
  onDeleteItem: (id: string) => void;
  onHeaderCellClick: (value: string) => void;
  onEditItem?: (data: IMortgage) => void;
  reset?: () => void;
  role: Roles;
};

const permissionOptionsObj = Object.entries(EPermissionApiKey).reduce(
  (acc, [label, value]) => {
    acc[value] = label;
    return acc;
  },
  {} as Record<string, string>
);

export const GetColumns = ({
  onDeleteItem,
  onHeaderCellClick,
  onEditItem,
  role,
}: Columns) => [
  {
    title: <HeaderCell title="name" sortable align="left" />,
    onHeaderCell: () => onHeaderCellClick("name"),
    dataIndex: "name",
    key: "name",
    render: (name: string) => {
      return <>{name}</>;
    },
  },
  {
    title: <HeaderCell title="Permissions" align="left" />,
    dataIndex: "permissions",
    key: "permissions",
    render: (permissions: EPermissionApiKey[]) => {
      return (
        <div className={"flex flex-col gap-2"}>
          {permissions.map((permission, index: number) => {
            return <span key={index}>{permissionOptionsObj[permission]}</span>;
          })}
        </div>
      );
    },
  },
  {
    title: <HeaderCell title="Allowed IPs" align="left" />,
    dataIndex: "allowedIPs",
    key: "allowedIPs",
    render: (allowedIPs: string[]) => {
      return (
        <div className={"flex flex-col gap-1"}>
          {allowedIPs.map((item: string, index: number) => {
            return <span key={index}>{item}</span>;
          })}
        </div>
      );
    },
  },
  {
    title: (
      <HeaderCell
        className="ps-3"
        title={<span className="whitespace-nowrap">Actions</span>}
      />
    ),
    dataIndex: "action",
    key: "action",
    width: 120,
    render: (_: string, row: IMortgage) => {
      if (role === Roles.ADMIN) {
        return (
          <ActionAdmin
            onDeleteItem={() => onDeleteItem?.(row.id)}
            row={row}
            onEditItem={onEditItem}
          />
        );
      }
    },
  },
];

const ActionAdmin = ({
  onDeleteItem,
  row,
  onEditItem,
}: {
  onDeleteItem: () => void;
  onEditItem?: (data: IMortgage) => void;
  row: IMortgage;
}) => {
  const { profile } = useProfile();
  return (
    <div className="flex items-center justify-center gap-3 pe-4">
      {profile?.role === Roles.ADMIN && (
        <>
          <Tooltip
            size="sm"
            content={"Edit Mortgage"}
            placement="top"
            color="invert"
          >
            <ActionIcon
              size="sm"
              variant="outline"
              onClick={() => {
                if (typeof onEditItem === "function") {
                  onEditItem(row);
                }
              }}
            >
              <PencilIcon className="h-4 w-4" />
            </ActionIcon>
          </Tooltip>
          <DeletePopover
            title={`Delete API Key`}
            description={`Are you sure to delete this API Key?`}
            onDelete={onDeleteItem}
          />
        </>
      )}
    </div>
  );
};
